/**
 * New chat modal component.
 * Allows creating direct or group conversations.
 */
import React, { useState, useEffect } from 'react';
import { X, User, Search } from 'lucide-react';
import { ChatRoom } from '../../services/chat';
import chatService from '../../services/chat';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface NewChatModalProps {
  onClose: () => void;
  onRoomCreated: (room: ChatRoom) => void;
}

interface UserOption {
  id: number;
  name: string;
  email: string;
  type: 'user' | 'employee' | 'client';
  avatar?: string;
}

const NewChatModal: React.FC<NewChatModalProps> = ({ onClose, onRoomCreated }) => {
  // Group creation removed: only direct chats
  const [_chatType, _setChatType] = useState<'direct'>('direct');
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  // Removed: groupName/groupDescription
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const isBuyer = role === 'client';

  // Charger les utilisateurs disponibles
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      // Si client: ne charger que les admins/super_admins depuis /users/admins
      // Sinon: charger users, employees et buyers
      let usersRes: Promise<any> | null = api.get('/users/admins');
      let employeesRes: Promise<any> | null = isBuyer ? null : api.get('/employees');
      let buyersRes: Promise<any> | null = isBuyer ? null : api.get('/clients');

      const settled = await Promise.allSettled([
        usersRes as Promise<any>,
        employeesRes as any,
        buyersRes as any,
      ]);

      let usersData: any[] = [];
      let employeesData: any[] = [];
      let buyersData: any[] = [];

      if (settled[0].status === 'fulfilled') {
        const d = (settled[0] as PromiseFulfilledResult<any>).value.data;
        usersData = Array.isArray(d) ? d : (d?.items || d?.data || []);
      } else {
        const status = (settled[0] as any)?.reason?.response?.status;
        if (status && status !== 404) {
          console.warn('[chat] /users request failed:', status, (settled[0] as any)?.reason?.response?.data);
        } else {
          console.info('[chat] /users endpoint not found (ignored).');
        }
      }

      if (!isBuyer && settled[1]?.status === 'fulfilled') {
        const d = (settled[1] as PromiseFulfilledResult<any>).value.data;
        employeesData = Array.isArray(d) ? d : (d?.items || d?.data || []);
      } else if (!isBuyer) {
        console.error('[chat] /employees request failed:', (settled[1] as any)?.reason?.response?.data || settled[1]);
        toast.error('Erreur lors du chargement des employés');
      }

      if (!isBuyer && settled[2]?.status === 'fulfilled') {
        const d = (settled[2] as PromiseFulfilledResult<any>).value.data;
        buyersData = Array.isArray(d) ? d : (d?.items || d?.data || []);
      } else if (!isBuyer) {
        // Non bloquant: endpoint réservé admin/super_admin
        const status = (settled[2] as any)?.reason?.response?.status;
        if (status && status !== 403 && status !== 401 && status !== 404) {
          console.warn('[chat] /clients request failed:', status, (settled[2] as any)?.reason?.response?.data);
        }
      }

      // Si client: ne garder que les admins/super_admins dans usersData
      const filteredUsersData = isBuyer
        ? usersData.filter((u: any) => {
            const r = (u.role || '').toLowerCase();
            return r === 'admin' || r === 'super_admin';
          })
        : usersData;

      const userOptions: UserOption[] = [
        ...filteredUsersData.map((u: any) => ({
          id: u.id,
          name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
          email: u.email,
          type: 'user' as const,
        })),
        ...(!isBuyer
          ? employeesData.map((e: any) => ({
              id: e.id,
              name: `${e.first_name} ${e.last_name}`,
              email: e.email,
              type: 'employee' as const,
              avatar: e.profile_picture,
            }))
          : []),
        ...(!isBuyer
          ? buyersData.map((b: any) => ({
              id: b.id,
              name: b.company_name || b.contact_name || b.email,
              email: b.email,
              type: 'client' as const,
              avatar: undefined,
            }))
          : []),
      ];

      setUsers(userOptions);
    } catch (error: any) {
      console.error('Error loading users/employees:', error);
      toast.error(`Erreur: ${error?.response?.data?.detail || error.message}`);
    } finally {
      setLoadingUsers(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    // Si client: ne garder que les admins/super_admins
    if (isBuyer && user.type !== 'user') return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query)
    );
  });

  // Buyers are merged into users list; no separate buyers array needed.

  const toggleUserSelection = (user: UserOption) => {
    setSelectedUsers([user]);
  };

  const handleCreate = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Veuillez sélectionner au moins un participant');
      return;
    }

    try {
      setLoading(true);

      let room: ChatRoom;

      const target = selectedUsers[0];
      room = await chatService.createDirectRoom({
        target_user_id: target.type === 'user' ? target.id : undefined,
        target_employee_id: target.type === 'employee' ? target.id : undefined,
        target_buyer_id: target.type === 'client' ? target.id : undefined,
      });

      toast.success(
        'Conversation créée'
      );
      onRoomCreated(room);
    } catch (error: any) {
      console.error('Error creating room:', error);
      toast.error('Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Nouvelle conversation
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Type selector removed - only direct chats */}
          <div className="flex space-x-2 mt-4">
            <div className="flex-1 px-4 py-2 rounded-lg bg-purple-100 text-purple-700 font-medium flex items-center justify-center space-x-2">
              <User className="h-5 w-5" />
              <span>Conversation directe</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Group info removed */}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un utilisateur..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Selected users */}
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedUsers.map((user) => (
                <div
                  key={`${user.type}-${user.id}`}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full"
                >
                  <span className="text-sm font-medium">{user.name}</span>
                  <button
                    onClick={() => toggleUserSelection(user)}
                    className="hover:text-purple-900"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* User list */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">
              Sélectionnez un utilisateur
            </p>
            
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {loadingUsers ? (
                <div className="text-center py-8 text-gray-500">
                  Chargement des utilisateurs...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchQuery ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur disponible'}
                </div>
              ) : (
                filteredUsers.map((user) => {
                const isSelected = selectedUsers.some(
                  (u) => u.id === user.id && u.type === user.type
                );

                return (
                  <button
                    key={`${user.type}-${user.id}`}
                    onClick={() => toggleUserSelection(user)}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                      isSelected
                        ? 'bg-purple-50 border-2 border-purple-600'
                        : 'hover:bg-gray-50 border-2 border-transparent'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                      {user.avatar ? (
                        <img
                          src={user.avatar}
                          alt={user.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <User className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                    
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>

                    {isSelected && (
                      <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          
          <button
            onClick={handleCreate}
            disabled={loading || selectedUsers.length === 0}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Création...' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewChatModal;
